/**
 * Cloudinary Upload Utility
 * Handles file uploads to Cloudinary cloud storage with automatic optimization
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get optimized Cloudinary URL with transformations
 * @param {string} url - Original Cloudinary URL
 * @param {object} options - Transformation options
 * @returns {string} Optimized URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  
  const {
    width = null,
    height = null,
    quality = 'auto',
    format = 'auto',
    crop = 'fill',
    gravity = 'auto'
  } = options;
  
  // Build transformation string
  const transforms = [];
  if (width) transforms.push(`w_${width}`);
  if (height) transforms.push(`h_${height}`);
  if (crop && (width || height)) transforms.push(`c_${crop}`);
  if (gravity && crop === 'fill') transforms.push(`g_${gravity}`);
  transforms.push(`q_${quality}`);
  transforms.push(`f_${format}`);
  
  const transformString = transforms.join(',');
  
  // Insert transformation into URL
  // URL format: https://res.cloudinary.com/{cloud}/image/upload/{existing_transforms}/{public_id}
  const uploadIndex = url.indexOf('/upload/');
  if (uploadIndex === -1) return url;
  
  const beforeUpload = url.substring(0, uploadIndex + 8); // includes '/upload/'
  const afterUpload = url.substring(uploadIndex + 8);
  
  // Check if there's already a version number (v1234567890)
  const versionMatch = afterUpload.match(/^v\d+\//);
  if (versionMatch) {
    // Insert transformation before version
    return `${beforeUpload}${transformString}/${afterUpload}`;
  }
  
  return `${beforeUpload}${transformString}/${afterUpload}`;
};

/**
 * Get thumbnail URL (small, optimized for lists)
 */
export const getThumbnailUrl = (url, size = 150) => {
  return getOptimizedImageUrl(url, {
    width: size,
    height: size,
    crop: 'fill',
    gravity: 'auto',
    quality: 'auto:low'
  });
};

/**
 * Get medium-sized image URL (for cards, previews)
 */
export const getMediumImageUrl = (url, width = 400) => {
  return getOptimizedImageUrl(url, {
    width: width,
    crop: 'scale',
    quality: 'auto:good'
  });
};

/**
 * Get large/hero image URL
 */
export const getLargeImageUrl = (url, width = 1200) => {
  return getOptimizedImageUrl(url, {
    width: width,
    crop: 'scale',
    quality: 'auto:best'
  });
};

/**
 * Upload a file to Cloudinary via backend
 * @param {File} file - The file to upload
 * @param {string} folder - Target folder (companies, news, ads, users, certifications, gallery)
 * @param {string} token - Auth token
 * @param {function} onProgress - Progress callback (optional)
 * @returns {Promise<{url: string, public_id: string}>}
 */
export const uploadToCloudinary = async (file, folder = 'companies', token, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  try {
    const response = await fetch(`${API_BASE_URL}/api/cloudinary/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }

    const result = await response.json();
    return {
      url: result.url,
      public_id: result.public_id,
      format: result.format,
      width: result.width,
      height: result.height,
      size: result.size
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

/**
 * Upload directly to Cloudinary using signed upload (for larger files)
 * @param {File} file - The file to upload
 * @param {string} folder - Target folder
 * @param {string} token - Auth token
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{url: string, public_id: string}>}
 */
export const uploadDirectToCloudinary = async (file, folder = 'companies', token, onProgress) => {
  // First, get signature from backend
  const sigResponse = await fetch(
    `${API_BASE_URL}/api/cloudinary/signature?folder=${folder}&resource_type=image`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  if (!sigResponse.ok) {
    throw new Error('Failed to get upload signature');
  }

  const sig = await sigResponse.json();

  // Upload directly to Cloudinary
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sig.api_key);
  formData.append('timestamp', sig.timestamp);
  formData.append('signature', sig.signature);
  formData.append('folder', sig.folder);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const result = JSON.parse(xhr.responseText);
        resolve({
          url: result.secure_url,
          public_id: result.public_id,
          format: result.format,
          width: result.width,
          height: result.height,
          size: result.bytes
        });
      } else {
        reject(new Error('Upload failed'));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('POST', `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
    xhr.send(formData);
  });
};

/**
 * Delete a file from Cloudinary
 * @param {string} publicId - The public_id of the file to delete
 * @param {string} token - Auth token
 */
export const deleteFromCloudinary = async (publicId, token) => {
  const response = await fetch(`${API_BASE_URL}/api/cloudinary/delete?public_id=${encodeURIComponent(publicId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Delete failed');
  }

  return response.json();
};

export default {
  uploadToCloudinary,
  uploadDirectToCloudinary,
  deleteFromCloudinary,
  getOptimizedImageUrl,
  getThumbnailUrl,
  getMediumImageUrl,
  getLargeImageUrl
};
